import {
  createFileRoute,
  useParams,
  Link,
  useNavigate,
} from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DestinationForm } from '@/components/destination/DestinationForm';
import { useDestinationMutations } from '@/hooks/useDestinations';
import { DestinationsService } from '@/services/destinations';
import type { Destination } from '../../../../../shared/types/seo';

export const Route = createFileRoute('/admin/destinations/$destinationId/edit')(
  {
    component: EditDestinationPage,
  }
);

function EditDestinationPage() {
  const { destinationId } = useParams({
    from: '/admin/destinations/$destinationId/edit',
  });
  const navigate = useNavigate();

  const [destination, setDestination] = useState<Destination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { createDestination, updateDestination, createError, updateError } =
    useDestinationMutations();

  const isNew = destinationId === 'new';

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }

    const loadDestination = async () => {
      try {
        const dest = await DestinationsService.getDestination(destinationId);
        setDestination(dest);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load destination'
        );
      } finally {
        setLoading(false);
      }
    };

    loadDestination();
  }, [destinationId, isNew]);

  const handleSubmit = async (values: Destination) => {
    if (isNew) {
      await createDestination(values);
    } else {
      await updateDestination({ id: destinationId, updates: values });
    }
    navigate({ to: '/admin/destinations' });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <Link to="/admin/destinations">
            <Button variant="outline" className="mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Destinations
            </Button>
          </Link>

          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/admin/destinations">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Destinations
          </Button>
        </Link>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">
            {isNew ? 'Add New Destination' : 'Edit Destination'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {isNew
              ? 'Create a new destination for the travel planning system'
              : `Editing destination: ${destination?.city || destinationId}`}
          </p>
        </div>

        {/* Error Messages */}
        {(createError || updateError) && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>
              {createError?.message || updateError?.message}
            </AlertDescription>
          </Alert>
        )}

        {/* Destination Form */}
        <DestinationForm
          initialValues={destination || undefined}
          onSubmit={handleSubmit}
          mode={isNew ? 'add' : 'edit'}
        />
      </div>
    </div>
  );
}
